import type { ScaleLinear } from 'd3-scale';
import { useSpring, animated } from '@react-spring/web';
import { shade } from '@mentimeter/ragnar-colors';
import { useState } from 'react';
import { getContrastingTextColor } from '../utils/colors';
import { BAR_HEIGHT_FACTOR, MIN_PADDING } from './constants';
import type { ScalesScore } from './Scales';

interface ScalesDistributionProps {
  fill: string;
  outlineColor: string;
  scores: ScalesScore;
  xScale: ScaleLinear<number, number>;
  height: number;
  isVisible: boolean;
  disableAnimations?: boolean;
  onScaleValueClick: ((value: number) => void) | undefined;
  activeValue: number | null;
}

const INACTIVE_COLOR_SHADE = 0.8;
const HOVER_COLOR_SHADE = 0.64;

export const ScalesDistribution = ({
  fill,
  outlineColor,
  scores,
  xScale,
  height,
  isVisible,
  disableAnimations = false,
  onScaleValueClick,
  activeValue,
}: ScalesDistributionProps) => {
  const [hoveredValue, setHoveredValue] = useState<number | null>(null);
  const circleRadius = Math.min(height * 0.2, 80);
  const barHeight = height * BAR_HEIGHT_FACTOR;

  const { r, opacity } = useSpring({
    r: isVisible ? circleRadius : circleRadius / 2,
    opacity: isVisible ? 1 : 0,
    immediate: disableAnimations,
  });

  return (
    <animated.g
      data-testid="scales-distribution"
      style={{
        opacity,
      }}
    >
      {Object.entries(scores).map(([key, score]) => {
        const index = parseInt(key, 10);

        const isHovered = hoveredValue === index;
        const isActive = activeValue === index;
        const color = isActive
          ? fill
          : shade(
              fill,
              '#FFFFFF',
              isHovered ? HOVER_COLOR_SHADE : INACTIVE_COLOR_SHADE,
            );

        const fontSize = Math.min(circleRadius * 0.9, 40);

        if (score === 0) {
          return null;
        }
        return (
          <g
            key={`score-${index}`}
            data-testid={`scale-value-${score}`}
            onClick={() => onScaleValueClick?.(index)}
            style={{
              cursor: onScaleValueClick !== undefined ? 'pointer' : 'unset',
            }}
          >
            <animated.circle
              cx={xScale(index)}
              cy={height - barHeight / 2}
              r={r}
              fill={color}
              style={{
                transition: 'fill 0.2s',
              }}
              stroke={outlineColor}
              strokeWidth={MIN_PADDING}
              onMouseEnter={() => setHoveredValue(index)}
              onMouseOver={() => setHoveredValue(index)}
              onMouseLeave={() => setHoveredValue(null)}
            />
            <text
              x={xScale(index)}
              y={height - barHeight / 2}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={fontSize}
              fill={getContrastingTextColor(color)}
              pointerEvents="none"
            >
              {score}
            </text>
          </g>
        );
      })}
    </animated.g>
  );
};
